import cogMascot from "../assets/cog-mascot.png";
import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
} from "../theme/mainPageVisual";
import {
  isUploadProgressTerminal,
  resolveUploadProgressPresentation,
  type UploadProgressStage,
} from "./cognitoryLibraryUploadStage";
import type { UploadAttemptLifecycle } from "./cognitoryLibraryUploadLifecycle";
import "./CognitoryLibraryShell.css";

const STAGE_ORDER: UploadProgressStage[] = [
  "uploading",
  "preparing",
  "structuring",
  "indexing",
  "ready",
];

function stageIndex(stage: UploadProgressStage): number {
  if (stage === "failed") {
    return -1;
  }
  return STAGE_ORDER.indexOf(stage);
}

function resolveStageLabel(stage: UploadProgressStage): string {
  switch (stage) {
    case "uploading":
      return "Uploading";
    case "preparing":
      return "Preparing";
    case "structuring":
      return "Reading";
    case "indexing":
      return "Indexing";
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
  }
}

export type CognitoryLibraryUploadStatusPanelProps = {
  filename: string;
  httpUploading: boolean;
  document?: GovernedLibraryDocumentSummary | null;
  timedOut?: boolean;
  uploadLifecycle?: UploadAttemptLifecycle | null | undefined;
};

export function CognitoryLibraryUploadStatusPanel({
  filename,
  httpUploading,
  document,
  timedOut,
  uploadLifecycle,
}: CognitoryLibraryUploadStatusPanelProps) {
  const presentation = resolveUploadProgressPresentation({
    filename,
    httpUploading,
    document: document ?? null,
    ...(timedOut !== undefined ? { timedOut } : {}),
    ...(uploadLifecycle !== undefined ? { uploadLifecycle } : {}),
  });
  const activeIndex = stageIndex(presentation.stage);
  const showStepper =
    presentation.stage !== "failed" && !timedOut && activeIndex >= 0;
  const cogActive =
    presentation.stage !== "ready" && presentation.stage !== "failed";

  return (
    <div
      className="cognitory-library-upload-status"
      data-testid="cognitory-library-upload-status"
      aria-live="polite"
      aria-busy={!isUploadProgressTerminal(presentation.stage)}
      style={{ fontFamily: MAIN_PAGE_FONT_STACK }}
    >
      <div className="cognitory-library-upload-status-header">
        <img
          src={cogMascot}
          alt=""
          aria-hidden="true"
          className={`cognitory-library-upload-status-cog${cogActive ? " cognitory-library-upload-status-cog--active" : ""}`}
          data-testid="cognitory-library-upload-status-cog"
        />
        <div className="cognitory-library-upload-status-copy">
          <p
            data-testid="cognitory-library-upload-status-filename"
            className="cognitory-library-upload-status-filename"
            style={{ color: MAIN_PAGE_COLORS.inkStrong }}
          >
            {filename}
          </p>
          <p
            data-testid="cognitory-library-upload-status-primary"
            className="cognitory-library-upload-status-primary"
            style={{ color: MAIN_PAGE_COLORS.slate }}
          >
            {presentation.stageText}
          </p>
          {presentation.secondaryText ? (
            <p
              data-testid="cognitory-library-upload-status-secondary"
              className="cognitory-library-upload-status-secondary"
              style={{ color: MAIN_PAGE_COLORS.soft }}
            >
              {presentation.secondaryText}
            </p>
          ) : null}
        </div>
      </div>

      {showStepper ? (
        <ol
          className="cognitory-library-upload-status-track"
          data-testid="cognitory-library-upload-status-steps"
          aria-label="Upload progress"
        >
          {STAGE_ORDER.map((stage, index) => {
            const state =
              index < activeIndex
                ? "complete"
                : index === activeIndex
                  ? "active"
                  : "upcoming";
            return (
              <li
                key={stage}
                className={`cognitory-library-upload-status-track-step cognitory-library-upload-status-track-step--${state}`}
                data-stage={stage}
                data-state={state}
                aria-current={state === "active" ? "step" : undefined}
              >
                <span className="cognitory-library-upload-status-track-dot-wrap">
                  <span
                    className="cognitory-library-upload-status-track-dot"
                    aria-hidden="true"
                  />
                  <span className="cognitory-library-upload-status-track-sr">
                    {resolveStageLabel(stage)}
                  </span>
                </span>
                {index < STAGE_ORDER.length - 1 ? (
                  <span
                    className="cognitory-library-upload-status-track-line"
                    aria-hidden="true"
                  />
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
}
